import { FC, ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaExpandablePreview } from 'raa-react';

import { DrawerSection } from 'components/drawerSections/DrawerSections';

import { SkyddstypEnum } from 'enums';

import { renderWithParagraphs } from 'utilities/renderWithParagraphs';

interface BebyggelseSkyddstypSectionProps {
  motivering?: string;
  karaktarsdrag?: string;
  skyddstyp?: SkyddstypEnum;
  lokal_klassificering?: string;
  ansvarig_myndighet?: string;
  sammansattParentId?: string | null;
  sammansattParentNamn?: string | null;
  isPublicerad?: boolean;
  skydd_subtyp?: string | null;
}

const BebyggelseSkyddstypSection: FC<BebyggelseSkyddstypSectionProps> = ({
  skyddstyp,
  skydd_subtyp,
  isPublicerad,
  lokal_klassificering,
  karaktarsdrag,
  sammansattParentId,
  sammansattParentNamn,
  motivering,
  ansvarig_myndighet
}: BebyggelseSkyddstypSectionProps): ReactElement => {
  const domaner = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner.data);

  return (
    <DrawerSection>
      <h2 className="tw-heading-md tw-mb-4">
        {domaner?.skyddstyper.find((doman) => doman.skyddstyp === skyddstyp)?.namn}
      </h2>
      <p className="tw-font-semibold">
        {domaner?.skyddstyper.find((doman) => doman.skyddstyp === skyddstyp)?.beskrivning}
      </p>

      <table className="tw-mt-2 tw-shrink tw-grow tw-basis-auto">
        <tbody className="tw-align-top [&>tr>th]:tw-pr-2 [&>tr>th]:tw-text-left">
          {sammansattParentId && (
            <tr>
              <th className="tw-whitespace-nowrap">Objektet ingår i</th>
              <td>
                <Link
                  className="raa-link"
                  to={`${isPublicerad ? '/bebyggelseobjekt' : '/utkast'}/${sammansattParentId}`}
                >
                  {sammansattParentNamn || `Länk till sammansatt ${isPublicerad ? 'bebyggelseobjekt' : 'utkast'}`}
                </Link>
              </td>
            </tr>
          )}

          {skydd_subtyp && (
            <tr>
              <th className="tw-whitespace-nowrap">Typ</th>
              <td>{skydd_subtyp}</td>
            </tr>
          )}
          <tr>
            <th className="tw-whitespace-nowrap">Ansvarig myndighet</th>
            <td>{ansvarig_myndighet}</td>
          </tr>
          {lokal_klassificering && (
            <tr>
              <th>Lokal klassning</th>
              <td>{lokal_klassificering}</td>
            </tr>
          )}
        </tbody>
      </table>

      <RaaExpandablePreview height={94} ariaLabel="Motiveringar">
        <h3 className="tw-mt-1 tw-font-semibold">Motivering</h3>
        {sammansattParentId && <p className="tw-italic">Byggnadsminnets beslutsmotivering finns i ovanstående länk</p>}
        {renderWithParagraphs(motivering || 'Motivering ej registrerad. Kontakta ansvarig organisation.')}
      </RaaExpandablePreview>

      {karaktarsdrag && (
        <RaaExpandablePreview height={94} ariaLabel="Karaktärsdrag">
          <h3 className="tw-mt-1 tw-font-semibold">Karaktärsdrag</h3>
          {renderWithParagraphs(karaktarsdrag)}
        </RaaExpandablePreview>
      )}

      {sammansattParentId && (
        <>
          <h3 className="tw-mt-1 tw-font-semibold">Skyddsbestämmelser</h3>
          <p>Se ovanstående länk</p>
        </>
      )}
    </DrawerSection>
  );
};

export default BebyggelseSkyddstypSection;
